import React, {useContext, useEffect, useRef, useState, Children, cloneElement} from 'react';
import useCurrentOrg from "../helpers/useCurrentOrg";
import {Button, ColorPicker, Divider, Flex, List, message, Modal, Popconfirm, Popover, Space, Table, Tag, Typography, Row, Col, Input, Empty, ConfigProvider, Select as AntSelect} from "antd";
import {FieldArray, Formik, useFormikContext} from "formik";
import api from "../api";
import { handleApiError } from "../helpers/formatApiError";
import * as Yup from "yup";
import {
    CheckOutlined,
    EditOutlined,
    PlusOutlined,
    RollbackOutlined,
    SaveOutlined,
    SearchOutlined,
    MenuOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ImportOutlined,
    UploadOutlined,
    SelectOutlined
} from "@ant-design/icons";
import {Form, FormItem, Input as FormInput, Radio, Checkbox, Select, Switch, SubmitButton} from "formik-antd";
import FloatLabel from "../helpers/FloatLabel";
import {DeleteIcon} from "../helpers/icons";
import HelpPopover from "../HelpPopover";
import setTitle from "../helpers/setTitle";
import {useTranslation} from "react-i18next";
import _ from 'lodash';
import VerticalSpace from '../helpers/VerticalSpace';
import useCurrentUser from '../helpers/useCurrentUser';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactMarkdown from 'react-markdown';
import NoDragTextArea from '../widgets/NoDragTextArea';
import ConfigurableEntityIcon from "../configurable_entities/ConfigurableEntityIcon";
import {notifyCustomMetaFieldsChanged} from '../helpers/customMetaFieldsEvents';
import {AbilityContext} from '../helpers/Can';

const SortableRow = props => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
    });

    const style = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

const TableRow = props => {
    // Ant Design also renders its empty placeholder through this component.
    // A missing ID looks active to dnd-kit when no drag is in progress.
    if (props['data-row-key'] == null) return <tr {...props} />;

    return <SortableRow {...props} />;
};

export const customMetaFieldReorderPayload = (fields, activeId, overId, ownerType) => {
    const source = fields.find(field => field.id === activeId);
    const target = fields.find(field => field.id === overId);
    if (!source || !target || source.id === target.id) return null;

    return {
        custom_meta_field_id: source.id,
        owner_type: ownerType,
        newIndex: target.position,
    };
};

export const customMetaFieldFilterParams = (filters, query, groupsAllowed = true) => {
    const {custom_meta_groups, ...otherFilters} = filters
    return {...otherFilters, q: query, ...(groupsAllowed && custom_meta_groups?.length ? {custom_meta_group_id: custom_meta_groups} : {})}
}

export default ({ownerType='Asset', typeTitle='Meta', embedded=false})=>{
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg()
    const ability = useContext(AbilityContext)
    const canReadGroups = !!ability?.can('read', 'CustomMetaGroup')

    if (!embedded) setTitle(t(`custom-${ownerType.toLowerCase()}-fields`,`Custom ${typeTitle} Fields`));

    const [loading, setLoading] = useState(false)
    const [customMetaFields, setCustomMetaFields] = useState([])
    const [customMetaGroups, setCustomMetaGroups] = useState([])
    const [searchValue, setSearchValue] = useState('')
    const filterContext = JSON.stringify([currentOrg?.id, ownerType, canReadGroups])
    const [filterState, setFilterState] = useState({})
    const filters = filterState.context === filterContext ? filterState.values : {}
    const currentOrgIdRef = useRef(currentOrg?.id)
    currentOrgIdRef.current = currentOrg?.id
    const fetchGeneration = useRef(0)
    const fieldContextRef = useRef('')
    const reorderContextRef = useRef('')
    reorderContextRef.current = JSON.stringify([currentOrg?.id, ownerType, searchValue, filters])

    const fetchData = (filters = {}) => {
        const generation = ++fetchGeneration.current
        const organizationId = currentOrg?.id
        setLoading(true)
        
        const params = customMetaFieldFilterParams(filters, searchValue, canReadGroups && ownerType !== 'AccessRequest')

        api.get(`/api/custom_meta_fields?owner_type=${ownerType}`, { params }).then(res => {
            if (generation !== fetchGeneration.current || organizationId !== currentOrgIdRef.current) return
            setLoading(false)
            setCustomMetaFields(res.data)
        }).catch(err => {
            if (generation !== fetchGeneration.current || organizationId !== currentOrgIdRef.current) return
            setLoading(false)
            handleApiError(err)
        })
    }

    useEffect(()=>{
        const context = JSON.stringify([currentOrg?.id, ownerType, canReadGroups])
        let nextFilters = filters
        if (fieldContextRef.current !== context) {
            fieldContextRef.current = context
            setCustomMetaFields([])
            nextFilters = {}
            setFilterState({context, values: {}})
        }
        fetchData(nextFilters)
    }, [currentOrg?.id, ownerType, searchValue, canReadGroups])

    useEffect(() => {
        if (!currentOrg?.id || ownerType === 'AccessRequest' || !canReadGroups) {
            setCustomMetaGroups([])
            return
        }

        let active = true
        const organizationId = currentOrg.id
        setCustomMetaGroups([])
        api.get('/api/custom_meta_groups').then(res => {
            if (!active || organizationId !== currentOrgIdRef.current) return
            const groups = res.data.filter(group => (
                group.custom_meta_fields || []
            ).some(membership => membership.custom_meta_field?.owner_type === ownerType))

            setCustomMetaGroups(groups)
        }).catch(err => {
            if (active && organizationId === currentOrgIdRef.current) handleApiError(err)
        })
        return () => { active = false }
    }, [currentOrg?.id, ownerType, canReadGroups])

    const [newModalVisible, setNewModalVisible] = useState(false)
    const [importModalVisible, setImportModalVisible] = useState(false)
    const [selectedRowKeys, setSelectedRowKeys] = useState([])

    const groupFilterOptions = [
        {value: 'none', label: t('none', 'None')},
        ...customMetaGroups.map(group => ({
            value: group.id,
            label: group.name
        }))
    ]

    const groupDetailsFor = group => (
        customMetaGroups.find(customMetaGroup => `${customMetaGroup.id}` === `${group.id}`) || group
    )

    const groupPopoverContent = group => {
        const details = groupDetailsFor(group)
        const fields = (details.custom_meta_fields || [])
            .map(membership => membership.custom_meta_field || membership)
            .filter(field => field?.name)

        return (
            <Space direction="vertical" size={4} style={{maxWidth: 280}}>
                {details.description ? (
                    <Typography.Text>{details.description}</Typography.Text>
                ) : (
                    <Typography.Text type="secondary">{t('no-description', 'No description')}</Typography.Text>
                )}

                {fields.length > 0 && (
                    <div>
                        <Typography.Text strong>{t('fields', 'Fields')}</Typography.Text>
                        <List
                            size="small"
                            dataSource={fields}
                            renderItem={field => (
                                <List.Item style={{padding: '2px 0'}}>
                                    {field.name}
                                </List.Item>
                            )}
                        />
                    </div>
                )}
            </Space>
        )
    }

    const clickNew = ()=>{
        setNewModalVisible(true)
        setTimeout(()=>{
            if (autoFocusInput.current) {
                autoFocusInput.current.focus()
            }
        }, 100)
    }

    const autoFocusInput = useRef(null);

    const exportSelected = () => {
        if (selectedRowKeys.length === 0) return;

        api.post('/api/custom_meta_fields/export', { ids: selectedRowKeys }).then(res => {
            const dataStr = JSON.stringify(res.data, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = selectedRowKeys.length === 1
                ? `${customMetaFields.find(c => c.id === selectedRowKeys[0])?.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_custom_field.json`
                : `custom_fields_export_${selectedRowKeys.length}.json`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success(t('message-exported-custom-fields', 'Exported {{count}} custom field(s).', { count: selectedRowKeys.length }));
            setSelectedRowKeys([]);
        }).catch(err => {
            message.error(t('message-export-failed', 'Failed to export custom field(s).'));
            console.error(err);
        });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
    };

    const updated = cmf => {
        setCustomMetaFields(fields => fields.map(field => field.id === cmf.id ? cmf : field))
        notifyCustomMetaFieldsChanged(ownerType, currentOrg?.id)
    }

    const added = cmfs => {
        const newFields = Array.isArray(cmfs) ? cmfs : [cmfs]
        setCustomMetaFields(fields => [...fields, ...newFields])
        fetchData(filters)
        notifyCustomMetaFieldsChanged(ownerType, currentOrg?.id)
    }

    const destroy = cmf => {
        const organizationId = currentOrg?.id
        api.delete(`/api/custom_meta_fields/${cmf.id}`).then(res => {
            message.success(t(`message-custom-${ownerType.toLowerCase()}-field-removed`,`Custom ${typeTitle} Field removed.`))
            setCustomMetaFields(fields => fields.filter(field => field.id !== cmf.id))
            notifyCustomMetaFieldsChanged(ownerType, organizationId)
        })
    }
    
    const handleSearch = _.debounce((value) => {
        setSearchValue(value)
    }, 300)
    
    const columns = [
        {
            title: '',
            dataIndex: 'sort',
            width: 30,
            className: 'drag-visible',
            render: () => (
                <MenuOutlined
                    style={{
                        cursor: 'grab',
                        color: '#999',
                    }}
                />
            )
        },
        {
            title: t('enabled','Enabled').toProperCase(),
            dataIndex: 'enabled',
            filterMultiple: false,
            filters: [
                {text: t('enabled','Enabled').toProperCase(), value: 'true'},
                {text: t('disabled','Disabled').toProperCase(), value: 'false'},
            ],
            defaultFilteredValue: ['true'],
            render: (enabled)=> (
                <>{enabled && <CheckOutlined/>}</>
            )
        },
        {title: t('name','Name'), dataIndex: 'name'},
        {title: t('description','Description'), dataIndex: 'description'},
        {
            title: t('notes','Notes'),
            dataIndex: 'notes',
            width: 60,
            align: 'center',
            render: (notes) => (
                notes ? (
                    <Popover 
                        content={
                            <div style={{maxWidth: 400}}>
                                <ReactMarkdown>{notes}</ReactMarkdown>
                            </div>
                        } 
                        title={t('notes','Notes')}
                    >
                        <InfoCircleOutlined style={{cursor: 'pointer', color: '#1890ff'}} />
                    </Popover>
                ) : null
            )
        },
        {
            title: t('value','Value'),
            dataIndex: 'multi',
            filters: [
                {text: t('multi-value','Multi-Value'), value: 'true'},
                {text: t('single-value','Single-Value'), value: 'false'},
            ],
            render: (multi)=> (
                <>{multi ? t('multi-value','Multi-Value') : t('single-value','Single-Value')}</>
            )
        },
        {
            title: t('type','Type'),
            dataIndex: 'value_type',
            filters: [
                {text: t('text','Text'), value: 'text'},
                {text: t('date','Date'), value: 'date'},
                {text: t('datetime','Date and Time'), value: 'datetime'},
                {text: t('mg-guid','MG GUID'), value: 'asset_guid'},
            ],
            render: (value_type)=> (
                <>{t(value_type, value_type?.toUpperCase ? value_type.toUpperCase() : value_type)}</>
            )
        },
        {
            title: t('controlled-vocab','Controlled Vocab'),
            dataIndex: 'free',
            filters: [
                {text: t('free-text','Free Text'), value: 'true'},
                {text: t('controlled','Controlled'), value: 'false'},
            ],
            render: (free, cmf)=> (
                <>{free ? t('free-text','Free Text') : <Tag>{cmf.custom_meta_values_attributes?.length || 0}</Tag>}</>
            )
        },
        {
            title: t('action-menu','Action Menu'),
            dataIndex: 'enable_action_menu',
            filters: [
                {text: t('enabled','Enabled'), value: 'true'},
                {text: t('disabled','Disabled'), value: 'false'},
            ],
            render: (enable_action_menu)=> (
                <>{enable_action_menu && <CheckOutlined/>}</>
            )
        },
    ]
    
    // Only add the AI Enabled column if the org has bedrock credentials
    if (ownerType === 'Asset' && currentOrg?.has_bedrock_credentials) {
        columns.push({
            title: t('ai-enabled','AI Enabled'),
            dataIndex: 'enable_ai',
            filters: [
                {text: t('enabled','Enabled'), value: 'true'},
                {text: t('disabled','Disabled'), value: 'false'},
            ],
            render: (enable_ai)=> (
                <>{enable_ai && <CheckOutlined/>}</>
            )
        })
    }

    // Role visibility column
    const roleLabels = {
        'general': t('general-members','General Members'),
        'global_tagger': t('global-taggers','Global Taggers'),
        'global_library': t('global-library-managers','Global Library Managers'),
        'global_content': t('global-content-managers','Global Content Managers'),
        'admin': t('admins','Admins'),
    }
    columns.push({
        title: t('visibility','Visibility'),
        dataIndex: 'minimum_role_level',
        filters: [
            {text: t('all-roles','All Roles'), value: 'null'},
            {text: t('general-members','General Members'), value: 'general'},
            {text: t('global-taggers','Global Taggers'), value: 'global_tagger'},
            {text: t('global-library-managers','Global Library Managers'), value: 'global_library'},
            {text: t('global-content-managers','Global Content Managers'), value: 'global_content'},
            {text: t('admins','Admins'), value: 'admin'},
        ],
        render: (minimum_role_level)=> {
            if (!minimum_role_level) {
                return <Tag color="green">{t('all-roles','All Roles')}</Tag>
            }
            const roleName = roleLabels[minimum_role_level] || minimum_role_level
            return <Tag>{roleName} {t('and-above','and above')}</Tag>
        }
    })

    if (ownerType !== 'AccessRequest') {
        columns.push({
            title: t('group','Group'),
            dataIndex: 'custom_meta_groups',
            filters: canReadGroups ? groupFilterOptions.map(option => ({text: option.label, value: option.value})) : undefined,
            filteredValue: filters.custom_meta_groups || null,
            filterSearch: true,
            width: 180,
            render: (groups = []) => {
                const visibleGroups = groups.filter(group => group.name)

                if (visibleGroups.length === 0) {
                    return null
                }

                return (
                    <Space size={[0, 4]} wrap>
                        {visibleGroups.map(group => {
                            const details = groupDetailsFor(group)

                            return (
                                <Popover
                                    key={group.id}
                                    title={group.name}
                                    content={groupPopoverContent(group)}
                                    trigger={['hover', 'click']}
                                >
                                    <Tag style={{cursor: 'help'}}>
                                        {details.icon && <ConfigurableEntityIcon icon={details.icon}/>}
                                        {group.name}
                                    </Tag>
                                </Popover>
                            )
                        })}
                    </Space>
                )
            }
        })
    }

    columns.push({
        title: t('actions','Actions'),
        dataIndex: 'actions',
        width: 100,
        render: (_, cmf)=> (
            <Space style={{textAlign:'right', marginLeft:'auto'}}>
                <CustomMetaEditButton cmf={cmf} updated={updated} ownerType={ownerType} typeTitle={typeTitle}/>
                <Popconfirm title={t('confirm-delete-custom-meta-field','Delete Custom Meta Field?')} onConfirm={()=> destroy(cmf)}>
                    <Button><DeleteIcon/></Button>
                </Popconfirm>
            </Space>
        )
    })

    const handleTableChange = (pagination, filters) => {
        reorderContextRef.current = JSON.stringify([currentOrg?.id, ownerType, searchValue, filters])
        setFilterState({context: filterContext, values: filters})
        fetchData(filters)
    }
    
    // Configure the sensors for drag detection
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        }),
    );
    
    // Handle drag end event
    const onDragEnd = ({ active, over }) => {
        const request = customMetaFieldReorderPayload(customMetaFields, active.id, over?.id, ownerType);
        if (request) {
            const oldIndex = customMetaFields.findIndex(i => i.id === active.id);
            const newIndex = customMetaFields.findIndex(i => i.id === over?.id);
            
            if (oldIndex !== -1 && newIndex !== -1) {
                const newData = arrayMove(customMetaFields.slice(), oldIndex, newIndex);
                
                const organizationId = currentOrg?.id
                const requestContext = reorderContextRef.current
                api.put('/api/custom_meta_fields/update_position', request).then(res => {
                    if (organizationId !== currentOrgIdRef.current || requestContext !== reorderContextRef.current) return
                    message.success(t('message-sort-order-updated','Sort order updated!'))
                    notifyCustomMetaFieldsChanged(ownerType, organizationId)
                    fetchData(filters)
                }).catch(err => {
                    if (organizationId !== currentOrgIdRef.current || requestContext !== reorderContextRef.current) return
                    message.error('Failed to update sort order')
                    console.error(err)
                    fetchData(filters)
                });
                
                setCustomMetaFields(newData);
            }
        }
    };

    return (
        <div style={{paddingTop:'1em'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: '1em'}}>
                <Space wrap>
                    <Button onClick={clickNew} type={'primary'} icon={<PlusOutlined/>}>{t(`button-add-custom-${ownerType.toLowerCase()}-field`,`Add Custom ${typeTitle} Field`)}</Button>
                    <Button onClick={() => setImportModalVisible(true)} icon={<ImportOutlined/>}>{t('import','Import')}</Button>
                </Space>

                <Space wrap style={{maxWidth: '100%'}}>
                    <Input
                        placeholder={t('search-custom-fields','Search custom fields...')}
                        prefix={<SearchOutlined />}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{width: 250, maxWidth: '100%'}}
                    />
                </Space>
                
                <CustomMetaModal
                    modalVisible={newModalVisible}
                    setModalVisible={setNewModalVisible}
                    onSuccess={added}
                    autoFocusInput={autoFocusInput}
                    ownerType={ownerType}
                    typeTitle={typeTitle}
                />

                <ImportModal
                    visible={importModalVisible}
                    setVisible={setImportModalVisible}
                    onSuccess={added}
                    ownerType={ownerType}
                    typeTitle={typeTitle}
                />
            </div>

            <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={customMetaFields.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <ConfigProvider renderEmpty={() => <Empty description={t('no-custom-fields-yet','No custom fields yet.')} />}>
                        <Table
                            size={'small'}
                            pagination={false}
                            dataSource={customMetaFields}
                            columns={columns.map(column => column.filters ? {...column, filteredValue: Object.hasOwn(filters, column.dataIndex) ? filters[column.dataIndex] : column.defaultFilteredValue || null} : column)}
                            rowKey="id"
                            loading={loading}
                            scroll={{x: 'max-content'}}
                            rowSelection={rowSelection}
                            components={{
                                body: {
                                    row: TableRow
                                },
                            }}
                            onChange={handleTableChange}
                            bordered
                            footer={() => (
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                                    <Space>
                                        {selectedRowKeys.length > 0 && (
                                            <Button type="primary" icon={<ExportOutlined/>} onClick={exportSelected}>
                                                {t('export', 'Export')} {selectedRowKeys.length}
                                            </Button>
                                        )}
                                    </Space>
                                    <Typography.Text type="secondary">
                                        {t('total-count', 'Total: {{count}}', { count: customMetaFields.length })}
                                    </Typography.Text>
                                </div>
                            )}
                        />
                    </ConfigProvider>
                </SortableContext>
            </DndContext>
        </div>
    )
}

const AiModelSelectorModal = ({ value, onChange }) => {
    const {t} = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);

    const availableModels = window.Config?.aiModels || [];
    const currentModel = availableModels.find(m => m.id === value);

    // Format price for display
    const formatPrice = (price) => {
        if (price === 0 || price === undefined || price === null) return '-';
        if (price >= 0.001) {
            return `$${price.toFixed(4)}`;
        }
        return `$${price.toFixed(6)}`;
    };

    // Get effective price for sorting (use input price, or second price for duration-based)
    const getEffectivePrice = (model) => {
        if (model.isDurationBased) {
            return model.secondPrice || 0;
        }
        return model.inputPrice || 0;
    };

    // Get asset type tags for a model
    const getAssetTypeTags = (model) => {
        const tags = [];
        if (model.supportsImage) tags.push({ label: t('image', 'Image'), color: 'blue' });
        if (model.supportsVideo) tags.push({ label: t('video', 'Video'), color: 'purple' });
        if (model.supportsAudio) tags.push({ label: t('audio', 'Audio'), color: 'orange' });
        if (model.supportsDocument) tags.push({ label: t('document', 'Document'), color: 'cyan' });
        return tags;
    };

    const columns = [
        {
            title: t('model', 'Model'),
            key: 'name',
            width: 280,
            render: (_, model) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{model.name}</div>
                    {model.description && (
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            {model.description}
                        </Typography.Text>
                    )}
                </div>
            )
        },
        {
            title: t('types', 'Types'),
            key: 'assetTypes',
            width: 120,
            render: (_, model) => (
                <Space size={2} wrap>
                    {model.supportsImage && <Tag color="blue" style={{marginRight: 0}}>Img</Tag>}
                    {model.supportsVideo && <Tag color="purple" style={{marginRight: 0}}>Vid</Tag>}
                    {model.supportsAudio && <Tag color="orange" style={{marginRight: 0}}>Aud</Tag>}
                    {model.supportsDocument && <Tag color="cyan" style={{marginRight: 0}}>Doc</Tag>}
                </Space>
            )
        },
        {
            title: t('pricing', 'Pricing'),
            key: 'pricing',
            width: 160,
            sorter: (a, b) => getEffectivePrice(a) - getEffectivePrice(b),
            showSorterTooltip: { title: t('sort-by-price', 'Sort by price') },
            render: (_, model) => (
                <div style={{ fontSize: '12px' }}>
                    {model.isDurationBased ? (
                        <>
                            <div>{formatPrice(model.secondPrice)}/sec</div>
                            <div>{formatPrice(model.outputPrice)}/1K out</div>
                        </>
                    ) : (
                        <>
                            <div>{formatPrice(model.inputPrice)}/1K in</div>
                            <div>{formatPrice(model.outputPrice)}/1K out</div>
                        </>
                    )}
                </div>
            )
        },
        {
            title: '',
            key: 'action',
            width: 100,
            render: (_, model) => (
                model.id === value ? (
                    <Tag color="green">{t('current', 'Current')}</Tag>
                ) : (
                    <Button
                        size="small"
                        ghost
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => {
                            onChange(model.id);
                            setModalVisible(false);
                        }}
                    >
                        {t('select', 'Select')}
                    </Button>
                )
            )
        }
    ];

    // Deduplicate models by model_id (keep first occurrence)
    const uniqueModels = availableModels.filter((model, index, self) =>
        index === self.findIndex(m => m.id === model.id)
    );

    // Include deprecated model if currently selected but not in available models
    const modelExists = uniqueModels.some(m => m.id === value);
    const modelsToShow = !modelExists && value
        ? [{
            key: `deprecated-${value}`,
            id: value,
            name: `${value}`,
            description: t('deprecated-model', 'This model is deprecated'),
            supportsImage: false,
            supportsVideo: false,
            supportsAudio: false,
            supportsDocument: false,
            inputPrice: 0,
            outputPrice: 0,
            isDurationBased: false
          }, ...uniqueModels]
        : uniqueModels;

    return (
        <>
            <Button
                onClick={() => setModalVisible(true)}
                style={{ width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {currentModel?.name || value || t('select-model', 'Select a model...')}
                </span>
                <Space size={4} style={{ marginLeft: 8, flexShrink: 0 }}>
                    {currentModel && getAssetTypeTags(currentModel).map(tag => (
                        <Tag key={tag.label} color={tag.color} style={{ marginRight: 0 }}>{tag.label}</Tag>
                    ))}
                    <SelectOutlined />
                </Space>
            </Button>

            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                title={t('select-ai-model', 'Select AI Model')}
                footer={null}
                width={700}
            >
                <Table
                    dataSource={modelsToShow}
                    columns={columns}
                    rowKey="key"
                    pagination={false}
                    size="small"
                />
            </Modal>
        </>
    );
};

const CustomMetaEditButton = ({cmf, updated, ownerType, typeTitle})=>{
    const {t} = useTranslation();
    const [modalVisible, setModalVisible] = useState(false)

    return (
        <>
            <Button icon={<EditOutlined/>} onClick={()=> setModalVisible(true)}>{t('button-edit','Edit')}</Button>

            <CustomMetaModal cmf={cmf} modalVisible={modalVisible} setModalVisible={setModalVisible} onSuccess={updated} ownerType={ownerType} typeTitle={typeTitle} autoFocusInput={null} />
        </>
    )
}

export const CustomMetaModal = ({cmf = {free: false, multi: false, value_type: 'text', asset_types: ['image', 'video', 'audio', 'document'], source_data: 'asset', model: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0', prompt: '', destination: 'none', auto_run: false, enable_controlled_vocab_creation: false, enabled: true, notes: ''}, modalVisible, setModalVisible, onSuccess, autoFocusInput, ownerType, typeTitle})=>{
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg()
    const currentUser = useCurrentUser()

    return (
        (<Formik
            initialValues={cmf}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
                const method = cmf.id ? api.put : api.post;
                const payload = {
                    ...values,
                    owner_type: ownerType,
                    ...(ownerType === 'Asset' ? {} : {enable_ai: false, auto_run: false}),
                };

                method(`/api/custom_meta_fields/${cmf.id || ''}`, {custom_meta_field: payload}).then(res => {
                    actions.resetForm();
                    actions.setSubmitting(false);

                    if(cmf.id) {
                        message.success(t(`custom-${ownerType.toLowerCase()}-field-updated`,`Custom ${typeTitle} Field "{{name}}" updated.`, {name: values.name}))
                    } else {
                        message.success(t(`custom-${ownerType.toLowerCase()}-field-created`,`Custom ${typeTitle} Field "{{name}}" created.`, {name: values.name}))
                    }

                    onSuccess(res.data)
                    setModalVisible(false);
                }).catch((err)=>{
                    actions.setSubmitting(false);
                    handleApiError(err)
                })
            }}
            validationSchema={
                Yup.object({
                    name: Yup.string().required(''), // TODO: check uniqueness
                })
            }
        >
            {({values, submitForm, handleSubmit, dirty, setFieldValue, isSubmitting, resetForm}) => {
                return (
                    (<Modal
                        open={modalVisible}
                        onCancel={()=> setModalVisible(false)}
                        title={<>{cmf.id ? t('edit','Edit') : t('new','New')} Custom {typeTitle} Field</>}
                        zIndex={9999}
                        footer={
                            <Space direction={'horizontal'}>
                                <Button type={'primary'} onClick={submitForm} loading={isSubmitting}>
                                    <SaveOutlined/>
                                    {cmf.id ? t('update','Update') : t('create','Create')}
                                </Button>
                                <Popconfirm
                                    title={t('confirm-cancel-edit-and-reset','Cancel Edit and Reset?')}
                                    onConfirm={()=>{
                                        resetForm()
                                        setModalVisible(false);
                                    }}
                                >
                                    <Button>{t('button-cancel','Cancel')}</Button>
                                </Popconfirm>
                            </Space>
                        }
                    >
                        <Form>
                            <FormItem name='enabled'>
                                <Switch checkedChildren={t('enabled','Enabled').toProperCase()} unCheckedChildren={t('disabled','Disabled').toProperCase()} name='enabled'/>
                            </FormItem>

                            <FormItem required name='name' showValidateSuccess>
                                <FloatLabel label={t('name','Name')} name={'name'} value={values?.name} >
                                    <FormInput size={'large'} required name='name' ref={autoFocusInput} autoFocus autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <FormItem name='description'>
                                <FloatLabel label={t('description-optional','Description (optional)')} name={'description'} value={values?.description} >
                                    <NoDragTextArea size={'large'} name='description' autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <FormItem name='notes'>
                                <FloatLabel label={t('admin-notes-optional','Admin Notes (optional)')} name={'notes'} value={values?.notes} >
                                    <NoDragTextArea size={'large'} name='notes' autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <FormItem name='minimum_role_level' label={<>{t('minimum-role-visibility','Minimum Role Visibility')} <HelpPopover code={'custom-meta-field-visibility'}/></>}>
                                <Select name={'minimum_role_level'} allowClear placeholder={t('all-roles-default','All Roles (default)')}>
                                    <Select.Option value={'general'}>{t('general-members','General Members')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'global_tagger'}>{t('global-taggers','Global Taggers')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'global_library'}>{t('global-library-managers','Global Library Managers')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'global_content'}>{t('global-content-managers','Global Content Managers')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'admin'}>{t('admins','Admins')} {t('only','only')}</Select.Option>
                                </Select>
                            </FormItem>

                            {ownerType === 'Asset' && currentOrg?.has_bedrock_credentials && (
                                <>
                                    <Divider>{t('ai-settings', 'AI Settings')}</Divider>
                                    
                                    <FormItem name='enable_ai'>
                                        <Checkbox name={'enable_ai'}>{t('enable-ai','Enable AI for this field?')}</Checkbox>
                                    </FormItem>
                                    
                                    {values.enable_ai && (
                                        <>
                                            <FormItem name='model' label={<>{t('ai-model', 'AI Model')} <HelpPopover code={'custom-meta-ai-model'}/></>}>
                                                <AiModelSelectorModal
                                                    value={values.model}
                                                    onChange={(modelId) => setFieldValue('model', modelId)}
                                                />
                                            </FormItem>
                                            
                                            <FormItem name='prompt' label={<>{t('ai-prompt', 'AI Prompt')} <HelpPopover code={'custom-meta-ai-prompt'}/></>}>
                                                <NoDragTextArea 
                                                    rows={3} 
                                                    name={'prompt'} 
                                                    placeholder={t('ai-prompt-placeholder', 'Instructions for the AI model...')}
                                                />
                                            </FormItem>
                                            
                                            <FormItem name='source_data' label={<>{t('source-data', 'Source Data')} <HelpPopover code={'custom-meta-ai-source-data'}/></>}>
                                                <Select name={'source_data'}>
                                                    <Select.Option value={'asset'}>{t('asset-data', 'Asset Data')}</Select.Option>
                                                    <Select.Option value={'text'}>{t('asset-extracted-text', 'Extracted Text')}</Select.Option>
                                                </Select>
                                            </FormItem>
                                            
                                            <FormItem name='asset_types' label={<>{t('asset-types', 'Asset Types')} <HelpPopover code={'custom-meta-ai-asset-types'}/></>}>
                                                <Checkbox.Group name={'asset_types'}>
                                                    <Checkbox value={'image'}>{t('image', 'Images')}</Checkbox>
                                                    <Checkbox value={'video'}>{t('video', 'Videos')}</Checkbox>
                                                    <Checkbox value={'audio'}>{t('audio', 'Audio')}</Checkbox>
                                                    <Checkbox value={'document'}>{t('document', 'Documents')}</Checkbox>
                                                </Checkbox.Group>
                                            </FormItem>
                                            
                                            <FormItem name='destination' label={<>{t('ai-additional-destination-optional', 'Additional Destination (optional)')} <HelpPopover code={'custom-meta-ai-additional-destination'}/></>}>
                                                <Select name={'destination'}>
                                                    <Select.Option value={'none'}>{t('none', 'None')}</Select.Option>
                                                    <Select.Option value={'keywords'}>{t('keywords', 'Keywords')}</Select.Option>
                                                    <Select.Option value={'auto_tags'}>{t('auto-tags', 'Auto Tags')}</Select.Option>
                                                    <Select.Option value={'append_to_description'}>{t('append-to-description', 'Append to Description')}</Select.Option>
                                                    <Select.Option value={'ocr_content'}>{t('ocr-content', 'OCR Content')}</Select.Option>
                                                    <Select.Option value={'alt_text'}>{t('alt-text', 'Alt Text')}</Select.Option>
                                                </Select>
                                            </FormItem>
                                            
                                            <VerticalSpace size={'small'}>
                                                <Checkbox name={'auto_run'}>{t('auto-run', 'Auto-run on new uploads')} <HelpPopover code={'custom-meta-ai-auto-run'}/></Checkbox>
                                                
                                                {!values.free && (
                                                    <Checkbox name={'enable_controlled_vocab_creation'}>{t('enable-vocab-creation', 'Allow creating new values')} <HelpPopover code={'custom-meta-ai-enable-controlled-vocab-creation'}/></Checkbox>
                                                )}
                                            </VerticalSpace>
                                        </>
                                    )}
                                </>
                            )}

                            <Divider>{t('value-settings', 'Value Settings')}</Divider>

                            <FormItem name='multi'>
                                <Radio.Group name={'multi'}>
                                    <Radio value={false} style={{display:'block'}}>{t('single-value','Single-Value')}</Radio>
                                    <Radio value={true}>{t('multi-value','Multi-Value')?.toProperCase()}</Radio>
                                </Radio.Group>
                            </FormItem>


                            <FormItem name='free'>
                                <Checkbox name={'free'}>{t('free-text-input','Free text input?')} <HelpPopover code={'custom-meta-field-free'}/></Checkbox>
                            </FormItem>

                            <FormItem name='enable_rename'>
                                <Checkbox name={'enable_rename'}>{t('enable-rename','Available for file renaming?')} <HelpPopover code={'custom-meta-enable-rename'}/></Checkbox>
                            </FormItem>

                            {values.free && (
                                <FormItem name='value_type'>
                                    <label>{t('value-type','Value Type')}: <HelpPopover code={'custom-meta-field-value-type'}/></label>
                                    <br/>
                                    <Radio.Group name={'value_type'} disabled={cmf.id}>
                                        <Radio value={'text'} style={{display:'block'}}>{t('text','Text')}</Radio>
                                        <Radio value={'date'} style={{display:'block'}}>{t('date','Date')}</Radio>
                                        <Radio value={'datetime'} style={{display:'block'}}>{t('datetime','Date and Time')}</Radio>
                                        {ownerType === 'Asset' && <Radio value={'asset_guid'} style={{display:'block'}}>{t('mg-guid','MG GUID')}</Radio>}
                                    </Radio.Group>
                                </FormItem>
                            )}

                            {!values.free && (
                                <>
                                    <Divider>{t('controlled-vocabulary','Controlled Vocabulary')} <HelpPopover code={'custom-meta-values'}/></Divider>

                                    {!values.multi && (
                                        <FormItem name='enable_action_menu'>
                                            <Checkbox name={'enable_action_menu'}>{t('enable-action-menu','Show in Action Menu?')} <HelpPopover code={'custom-meta-enable-action-menu'}/></Checkbox>
                                        </FormItem>
                                    )}

                                    <FieldArray
                                        name='custom_meta_values_attributes'
                                        render={arrayHelpers => {
                                            const addNewValue = () => {
                                                const attribute = ['number', 'price'].includes(values.value_type) ? 'number' : ['date', 'datetime'].includes(values.value_type) ? 'date' : 'text';
                                                arrayHelpers.push({[attribute]: values.new_value});
                                                setFieldValue('new_value', null)
                                            }

                                            return (
                                                <>
                                                    <div style={{display:'flex'}}>
                                                        <Form.Item name={'new_value'} style={{flex:'auto', marginRight:'.5em'}}>
                                                            <FormInput name={'new_value'} placeholder={t('placeholder-enter-new-value...','Enter New Value...')} onKeyDown={e => { if(e.keyCode == 13) addNewValue() }} autoComplete={'off'}/>
                                                        </Form.Item>

                                                        <Button onClick={addNewValue} style={{flex:'none'}}><PlusOutlined/> {t('add','Add')}</Button>
                                                    </div>

                                                    <List
                                                        dataSource={values.custom_meta_values_attributes}
                                                        itemLayout={'horizontal'}
                                                        locale={{emptyText:t('no-values-yet','No values yet.')}}
                                                        size={'small'}
                                                        renderItem={cmv => <CustomMetaValue cmv={cmv} onRemove={() => arrayHelpers.remove(values.custom_meta_values_attributes.indexOf(cmv))}/>}
                                                    />
                                                </>
                                            );
                                        }}
                                    />
                                </>
                            )}
                        </Form>
                    </Modal>)
                );
            }}
        </Formik>)
    );
}

const CustomMetaValue = ({cmv, onRemove})=>{
    const {t} = useTranslation();
    const { values, setFieldValue } = useFormikContext();

    if(!values.custom_meta_values_attributes) return <></>

    const i = values.custom_meta_values_attributes?.indexOf(cmv)
    const attribute = ['number', 'price'].includes(values.value_type) ? 'number' : ['date', 'datetime'].includes(values.value_type) ? 'date' : 'text';
    const toDestroy = cmv._destroy

    return (
        <List.Item
            style={{display: cmv._destroy ? 'red' : 'auto'}}
            actions={[
                <Button
                    onClick={() => {
                        if (cmv.id) {
                            setFieldValue(`custom_meta_values_attributes[${i}]._destroy`, !toDestroy)
                        } else {
                            onRemove()
                        }
                    }}
                >
                    {toDestroy ? <RollbackOutlined/> : <DeleteIcon/>}
                </Button>
            ]}
        >
            <Flex align={'center'} gap={6} justify={'flex-start'}>
                <ColorPicker
                    disabledAlpha
                    style={{marginLeft:'auto'}}
                    value={values.custom_meta_values_attributes[i]?.color_label}
                    allowClear
                    onChange={(c, hex) => {
                        setFieldValue(`custom_meta_values_attributes[${i}].color_label`, hex);
                    }}
                />

                <Typography.Text
                    disabled={toDestroy}
                    editable={!toDestroy && {
                        onChange: val => {
                            setFieldValue(`custom_meta_values_attributes[${i}].${attribute}`, val)
                        }
                    }}
                >
                    {toDestroy && t('removing','Removing') + ': '}
                    {cmv[attribute] ?? ''}
                </Typography.Text>
            </Flex>
        </List.Item>
    );
}

const ImportModal = ({visible, setVisible, onSuccess, ownerType, typeTitle}) => {
    const {t} = useTranslation();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importing, setImporting] = useState(false);

    const handleImport = () => {
        if (!selectedFile) {
            message.error(t('message-no-file-selected', 'Please select a file to import.'));
            return;
        }

        setImporting(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        api.post('/api/custom_meta_fields/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
            const data = res.data;
            if (Array.isArray(data)) {
                message.success(t('message-custom-fields-imported', 'Imported {{count}} custom field(s).', { count: data.length }));
                onSuccess(data);
            } else {
                message.success(t('message-custom-field-imported', 'Custom field "{{name}}" imported.', { name: data.name }));
                onSuccess(data);
            }
            setSelectedFile(null);
            setImporting(false);
            setVisible(false);
        }).catch(err => {
            setImporting(false);
            message.error(err.data?.error || t('message-import-failed', 'Failed to import custom field.'));
        });
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setVisible(false);
    };

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            title={t('import-custom-field', `Import Custom ${typeTitle} Field`)}
            zIndex={9999}
            footer={
                <Space>
                    <Button
                        type="primary"
                        onClick={handleImport}
                        loading={importing}
                        disabled={!selectedFile}
                        icon={<ImportOutlined/>}
                    >
                        {t('import', 'Import')}
                    </Button>
                    <Button onClick={handleClose}>
                        {t('button-cancel', 'Cancel')}
                    </Button>
                </Space>
            }
        >
            <VerticalSpace>
                <Typography.Text type="secondary">
                    {t('import-instructions', 'Select a JSON file exported from another custom field to import its settings.')}
                </Typography.Text>

                <input
                    type="file"
                    ref={fileInputRef}
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                />
                <Button
                    icon={<UploadOutlined/>}
                    onClick={() => fileInputRef.current.click()}
                    style={{ width: '100%' }}
                >
                    {selectedFile ? selectedFile.name : t('select-json-file', 'Select JSON File...')}
                </Button>
            </VerticalSpace>
        </Modal>
    );
}
